<template>
  <b-modal
    :id="modalId"
    :ref="modalId"
    centered
    hide-footer
    :title="$t('Table Settings')"
    modal-class="custom-columns-modal"
    @hidden="resetSearch"
  >
    <div class="custom-columns-content">
      <div class="d-flex align-items-center justify-content-between mb-2">
        <p class="mb-0">
          {{ $t('Select the columns you want to display in the table.') }}
        </p>
      </div>

      <!-- Search and Select All -->
      <div class="d-flex align-items-center mb-2 search-container p-1 rounded bg-light">
        <b-input-group class="input-group-merge mr-1">
          <b-input-group-prepend is-text>
            <feather-icon icon="SearchIcon" />
          </b-input-group-prepend>
          <b-form-input
            v-model="searchQuery"
            :placeholder="$t('Search Columns')"
            class="search-input"
          />
        </b-input-group>
        <div class="d-flex align-items-center flex-shrink-0">
          <b-form-checkbox
            v-model="selectAll"
            switch
            class="custom-control-primary"
            @change="handleSelectAll"
          >
            <span class="small font-weight-bold">{{ $t('Select All') }}</span>
          </b-form-checkbox>
        </div>
      </div>

      <!-- Columns Grid -->
      <div class="columns-grid-wrapper custom-scrollbar">
        <b-row>
          <b-col
            v-for="option in filteredOptions"
            :key="option.value"
            cols="12"
            md="6"
            class="mb-1"
          >
            <div
              class="column-item p-1 border rounded d-flex align-items-center justify-content-between cursor-pointer"
              :class="{
                'border-primary bg-light-primary': localSelectedColumns.includes(option.value)
              }"
              @click="toggleColumn(option.value)"
            >
              <div class="d-flex align-items-center overflow-hidden">
                <feather-icon
                  :icon="option.icon || 'GridIcon'"
                  size="16"
                  class="mr-1 text-muted handle-icon"
                />
                <span class="text-truncate" :title="option.text">{{ option.text }}</span>
              </div>
              <b-form-checkbox
                :checked="localSelectedColumns.includes(option.value)"
                class="pointer-events-none"
                @change="toggleColumn(option.value)"
              />
            </div>
          </b-col>
        </b-row>
        <div v-if="filteredOptions.length === 0" class="text-center py-2 text-muted">
          {{ $t('No columns found') }}
        </div>
      </div>
    </div>

    <!-- Custom Footer -->
    <div class="d-flex align-items-center justify-content-end border-top pt-1 mt-1">
      <b-button variant="outline-secondary" class="mr-1" @click="hide">
        {{ $t('Cancel') }}
      </b-button>
      <b-button variant="primary" @click="save">
        {{ $t('Save Changes') }}
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal,
  BButton,
  BFormCheckbox,
  BInputGroup,
  BInputGroupPrepend,
  BFormInput,
  BRow,
  BCol
} from 'bootstrap-vue';

export default {
  name: 'CustomColumnsModal',
  components: {
    BModal,
    BButton,
    BFormCheckbox,
    BInputGroup,
    BInputGroupPrepend,
    BFormInput,
    BRow,
    BCol
  },
  props: {
    modalId: {
      type: String,
      default: 'custom-columns-modal'
    },
    options: {
      type: Array,
      required: true,
      default: () => []
    },
    value: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      localSelectedColumns: [],
      searchQuery: '',
      selectAll: false
    };
  },
  computed: {
    filteredOptions() {
      if (!this.searchQuery) return this.options;
      const query = this.searchQuery.toLowerCase();
      return this.options.filter(opt => opt.text.toLowerCase().includes(query));
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        this.localSelectedColumns = [...newVal];
        this.updateSelectAllState();
      }
    }
  },
  methods: {
    show() {
      this.$refs[this.modalId].show();
    },
    hide() {
      this.$refs[this.modalId].hide();
    },
    resetSearch() {
      this.searchQuery = '';
    },
    toggleColumn(value) {
      if (this.localSelectedColumns.includes(value)) {
        this.localSelectedColumns = this.localSelectedColumns.filter(c => c !== value);
      } else {
        this.localSelectedColumns.push(value);
      }
      this.updateSelectAllState();
    },
    handleSelectAll(val) {
      if (val) {
        // Select only currently filtered options if search is active, or all if not
        const visibleValues = this.filteredOptions.map(o => o.value);
        // Add visible values that aren't already selected
        const newSelection = [...this.localSelectedColumns];
        visibleValues.forEach(v => {
          if (!newSelection.includes(v)) newSelection.push(v);
        });
        this.localSelectedColumns = newSelection;
      } else {
        // Deselect currently filtered options
        const visibleValues = this.filteredOptions.map(o => o.value);
        this.localSelectedColumns = this.localSelectedColumns.filter(
          c => !visibleValues.includes(c)
        );
      }
    },
    updateSelectAllState() {
      if (this.filteredOptions.length === 0) {
        this.selectAll = false;
        return;
      }
      const visibleValues = this.filteredOptions.map(o => o.value);
      const allVisibleSelected = visibleValues.every(v => this.localSelectedColumns.includes(v));
      this.selectAll = allVisibleSelected;
    },
    save() {
      this.$emit('input', this.localSelectedColumns); // Sync v-model
      this.$emit('save', this.localSelectedColumns);
      this.hide();
    }
  }
};
</script>

<style lang="scss" scoped>
.custom-columns-modal {
  .modal-content {
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }
}

.custom-columns-content {
  .columns-grid-wrapper {
    max-height: 400px;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 4px;
  }

  .column-item {
    transition: all 0.2s ease;
    border: 1px solid #e9ecef;

    &:hover {
      border-color: #7367f0;
      background-color: #f8f8f8;
      transform: translateY(-1px);
    }

    &.border-primary {
      background-color: rgba(115, 103, 240, 0.08);
      border-color: #7367f0 !important;
    }
  }

  /* Scrollbar Styling */
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;

    &:hover {
      background: #a8a8a8;
    }
  }
}

.pointer-events-none {
  pointer-events: none;
}
</style>

<style lang="scss">
.custom-columns-modal {
  .modal-dialog {
    max-width: 550px !important;
  }
}

.dark-layout {
  .custom-columns-modal {
    .search-container {
      background-color: #161d31 !important;
    }

    .column-item {
      border-color: #3b4253;

      &:hover {
        background-color: #283046;
      }
    }
  }
}
</style>
